import jsPDF from 'jspdf';

const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN_X = 16;
const MARGIN_TOP = 20;
const MARGIN_BOTTOM = 18;
const CONTENT_W = PAGE_W - MARGIN_X * 2;

export interface RelatorioFinalStat {
  label: string;
  valor: string;
  cor?: [number, number, number];
}

export interface RelatorioFinalTabelaLinha {
  colunas: string[];
}

export interface RelatorioFinalTabela {
  titulo: string;
  cabecalho: string[];
  larguras: number[];
  linhas: RelatorioFinalTabelaLinha[];
  rodape?: string;
}

export interface RelatorioFinalDados {
  periodo: string;
  stats: RelatorioFinalStat[];
  tabelas: RelatorioFinalTabela[];
}

const COR_TITULO: [number, number, number] = [15, 23, 42];
const COR_MUTED: [number, number, number] = [100, 116, 139];
const COR_PRIMARIA: [number, number, number] = [31, 111, 159];
const COR_BORDA: [number, number, number] = [226, 232, 240];

let doc: jsPDF;
let y = MARGIN_TOP;

const novaPagina = () => {
  doc.addPage();
  y = MARGIN_TOP;
};

const garantirEspaco = (altura: number) => {
  if (y + altura > PAGE_H - MARGIN_BOTTOM) novaPagina();
};

const desenharStats = (stats: RelatorioFinalStat[]) => {
  const gap = 6;
  const largura = (CONTENT_W - gap * (stats.length - 1)) / stats.length;
  const altura = 22;
  garantirEspaco(altura + 4);
  stats.forEach((stat, index) => {
    const x = MARGIN_X + index * (largura + gap);
    doc.setDrawColor(...COR_BORDA);
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(x, y, largura, altura, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...COR_MUTED);
    doc.text(stat.label.toUpperCase(), x + largura / 2, y + 7, { align: 'center', maxWidth: largura - 6 });
    doc.setFontSize(12);
    doc.setTextColor(...(stat.cor || COR_TITULO));
    doc.text(stat.valor, x + largura / 2, y + 16, { align: 'center', maxWidth: largura - 6 });
  });
  y += altura + 10;
};

const desenharTabela = (tabela: RelatorioFinalTabela) => {
  garantirEspaco(16);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(...COR_TITULO);
  doc.text(tabela.titulo, MARGIN_X, y);
  y += 6;

  const desenharCabecalho = () => {
    doc.setFillColor(232, 243, 251);
    doc.rect(MARGIN_X, y - 4, CONTENT_W, 6.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...COR_PRIMARIA);
    let x = MARGIN_X + 2;
    tabela.cabecalho.forEach((coluna, index) => {
      doc.text(coluna, x, y);
      x += tabela.larguras[index];
    });
    y += 6.5;
  };

  desenharCabecalho();

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  tabela.linhas.forEach((linha, indice) => {
    garantirEspaco(6);
    if (y === MARGIN_TOP) desenharCabecalho();
    if (indice % 2 === 1) {
      doc.setFillColor(248, 250, 252);
      doc.rect(MARGIN_X, y - 4, CONTENT_W, 6, 'F');
    }
    doc.setTextColor(51, 65, 85);
    let x = MARGIN_X + 2;
    linha.colunas.forEach((valor, index) => {
      doc.text(valor, x, y, { maxWidth: tabela.larguras[index] - 3 });
      x += tabela.larguras[index];
    });
    y += 6;
  });

  if (tabela.rodape) {
    garantirEspaco(8);
    doc.setDrawColor(...COR_BORDA);
    doc.line(MARGIN_X, y - 3, MARGIN_X + CONTENT_W, y - 3);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...COR_TITULO);
    doc.text(tabela.rodape, MARGIN_X, y + 2);
    y += 8;
  }

  y += 6;
};

export const gerarRelatorioFinalPdf = (dados: RelatorioFinalDados): jsPDF => {
  doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  y = MARGIN_TOP;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...COR_TITULO);
  doc.text('Relatório Final — Colônia de Férias', MARGIN_X, y);
  y += 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...COR_MUTED);
  doc.text(
    `Período ${dados.periodo} • Gerado em ${new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date())}`,
    MARGIN_X,
    y
  );
  y += 10;

  desenharStats(dados.stats);

  dados.tabelas.forEach((tabela) => desenharTabela(tabela));

  const totalPaginas = doc.getNumberOfPages();
  for (let pagina = 1; pagina <= totalPaginas; pagina += 1) {
    doc.setPage(pagina);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COR_MUTED);
    doc.text('Colônia de Férias UBA Clube', MARGIN_X, PAGE_H - 10);
    doc.text(`Página ${pagina} de ${totalPaginas}`, PAGE_W - MARGIN_X, PAGE_H - 10, { align: 'right' });
  }

  return doc;
};

export const baixarRelatorioFinalPdf = (dados: RelatorioFinalDados) => {
  const documento = gerarRelatorioFinalPdf(dados);
  const data = new Date().toISOString().slice(0, 10);
  documento.save(`relatorio-final-${dados.periodo}-${data}.pdf`);
};
